import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  Beef, Leaf, Sparkles, Home, LayoutGrid,
  Apple, HeartPulse, Flower, ShoppingBag,
  Coffee, Wine, Carrot, Pill,
  Shirt, Baby, Bone, Sofa, Lightbulb, PawPrint,
} from 'lucide-react';
import { mainCategoriesAPI } from '@/lib/api';

// Map Lucide icon names (stored in DB) to imported components.
const ICONS = {
  Beef, Leaf, Sparkles, Home, LayoutGrid,
  Apple, HeartPulse, Flower, ShoppingBag,
  Coffee, Wine, Carrot, Pill,
  Shirt, Baby, Bone, Sofa, Lightbulb, PawPrint,
};

/**
 * Horizontal main-category strip rendered under the main Navbar.
 * Always shows 4 (or N) main categories like FOOD · HEALTH · BODY & BEAUTY · HOME & LIFESTYLE.
 */
export const CategoryMenu = () => {
  const [mains, setMains] = useState([]);
  const location = useLocation();
  const [params] = useSearchParams();
  const activeMain = params.get('main') || '';

  useEffect(() => {
    mainCategoriesAPI.list({ available: true })
      .then(r => setMains(r.data || []))
      .catch(() => setMains([]));
  }, []);

  if (!mains.length) return null;
  const onShop = location.pathname === '/shop';

  const renderItem = (key, to, label, IconComp, isActive, testId) => (
    <Link
      key={key}
      to={to}
      data-testid={testId}
      className={`group flex items-center gap-2 px-4 py-2 rounded-full transition-all flex-shrink-0 border ${
        isActive
          ? 'text-gold border-gold/50 bg-gold/10 shadow-[0_0_0_3px_rgba(212,168,67,0.08)]'
          : 'text-foreground/60 hover:text-foreground border-transparent hover:border-border'
      }`}
    >
      {IconComp ? (
        <IconComp
          className={`h-5 w-5 transition-transform group-hover:scale-110 ${isActive ? 'text-gold' : 'text-foreground/50'}`}
          strokeWidth={2}
        />
      ) : null}
      <span className="font-body text-xs sm:text-sm uppercase tracking-[0.15em] font-semibold">
        {label}
      </span>
    </Link>
  );

  return (
    <nav
      data-testid="category-menu"
      className="bg-background border-b border-border"
      aria-label="Main categories"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 sm:gap-3 lg:justify-center overflow-x-auto whitespace-nowrap py-3 no-scrollbar">
          {renderItem('all', '/shop', 'All', LayoutGrid, onShop && !activeMain, 'category-link-all')}
          {mains.map(m => {
            const IconComp = ICONS[m.icon] || null;
            return renderItem(
              m.id,
              `/shop?main=${m.id}`,
              m.name,
              IconComp,
              onShop && activeMain === m.id,
              `main-category-link-${m.slug}`
            );
          })}
        </div>
      </div>
    </nav>
  );
};
